import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
export type InstallPrompt = Event & {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};
const InstallContext = createContext<{
  installed: boolean;
  available: boolean;
  takePrompt(): InstallPrompt | null;
}>({ installed: false, available: false, takePrompt: () => null });
export const useInstallContext = () => useContext(InstallContext);
export function InstallProvider({ children }: PropsWithChildren) {
  const [installed, setInstalled] = useState(false);
  const [available, setAvailable] = useState(false);
  const prompt = useRef<InstallPrompt | null>(null);
  useEffect(() => {
    const media = matchMedia('(display-mode: standalone)');
    const update = () =>
      setInstalled(
        media.matches || Boolean((navigator as Navigator & { standalone?: boolean }).standalone),
      );
    const before = (event: Event) => {
      event.preventDefault();
      prompt.current = event as InstallPrompt;
      setAvailable(true);
    };
    const complete = () => {
      setInstalled(true);
      prompt.current = null;
      setAvailable(false);
    };
    update();
    media.addEventListener('change', update);
    window.addEventListener('beforeinstallprompt', before);
    window.addEventListener('appinstalled', complete);
    return () => {
      media.removeEventListener('change', update);
      window.removeEventListener('beforeinstallprompt', before);
      window.removeEventListener('appinstalled', complete);
    };
  }, []);
  return (
    <InstallContext.Provider
      value={{
        installed,
        available,
        takePrompt: () => {
          const value = prompt.current;
          prompt.current = null;
          setAvailable(false);
          return value;
        },
      }}
    >
      {children}
    </InstallContext.Provider>
  );
}
